import React from "react";

import style from "./publish.module.scss";
import { effect, useSignal } from "@preact/signals-react";
import useFeatureTags from "@custom-hooks/useFeatureTags";
import { Button } from "antd";
import { editorActions } from "@store/editor";
import { useDispatch } from "react-redux";
import { useHomeUsePluginContainer } from "@custom-hooks/HomeUsePluginRoot";
import { useHomeChildContainer } from "@custom-hooks/HomeUsePluginRoot/childContainer";
import { CheckedUIListType } from "@custom-hooks/HomeUsePluginRoot/state/useSaasAndThinkAppState";
import store from "@store/index";
import useObjectRelationGqlRequest from "@custom-hooks/useObjectRelationGqlRequest";
import { WEB_APP_DEFAULT_SUBTITLE, WEB_APP_DEFAULT_TITLE } from "@views/thinking-layout-editor/constants";
import { studioActions } from "@store/studio";
import func from "@uikit/func";
import { usePreviewAppStore } from "@custom-hooks/HomeUsePluginRoot/usePreviewAppStore";
import { PreviewAppTypeMap, getDefaultPreviewAppTypeList } from "../PreviewApp/const";
import { logEvent } from "@uikit/service/amplitude";
import { PreviewAppUtil } from "../PreviewApp/PreviewAppUtil";
import { settings } from "imagica-corekit/dist/base/kernel/Settings";

interface Props {
  backPreview: () => void;
  inOrOutPreview: (bol: boolean) => void;
}

const Publish: React.FC<Props> = props => {
  const { inOrOutPreview } = props;
  const featureTags = useFeatureTags();
  const dispatch = useDispatch();
  const {
    homeReactFlowState: { setSelectedUI },
    saasAndThinkState: { setSaasUIData, setCheckedUIList },
    isNewEdgeRunRef,
  } = useHomeUsePluginContainer();
  const { addPreview, getSelectedTemplate, assignPreviewAppData } = usePreviewAppStore();
  const { resetStartNodeIds, autoSelectTemplate, getUIListParam, handleSave } = useHomeChildContainer();
  const optionDataList = useSignal<PreviewApp.FormatChooseAppType[]>(
    getDefaultPreviewAppTypeList({
      visibleStaticWeb: featureTags.enableStaticMode,
      disabledChatAi: !featureTags.enableChatAiFlow,
    })
  );
  const selectedCardInfo = useSignal<PreviewApp.FormatChooseAppType | null>(null);

  const confirmLoading = useSignal(false);
  const needUpdate = useSignal(false);
  const { gql } = useObjectRelationGqlRequest();

  const setSelectedTemplate = (val: any) => {
    dispatch(studioActions.setSelectedTemplate(val));
  };

  effect(async () => {
    if (needUpdate.value) {
      needUpdate.value = false;
      const isStatic = selectedCardInfo.value?.isStaticMode ?? false;
      const arr = [
        {
          label: isStatic ? "Static Website" : "AI App",
          value: "saas",
          isStaticApp: isStatic,
        } as CheckedUIListType,
      ];

      let uilist: any[] = getUIListParam(arr);
      uilist.forEach(item => {
        item.title = WEB_APP_DEFAULT_TITLE;
        item.subTitle = WEB_APP_DEFAULT_SUBTITLE;
        item.input = [];
        item.output = [];
        item.staticMode = isStatic;
      });

      try {
        confirmLoading.value = true;
        const selectedTemplate: any = store.getState().studio.selectedTemplate;

        let sameTemplate = false;
        const ui = isNewEdgeRunRef.current ? selectedTemplate.v3?.UI : selectedTemplate.v2?.UI;
        if (!func.isEmpty(ui)) {
          sameTemplate = ui[0].staticMode === uilist[0].staticMode && ui[0].value === uilist[0].value;
          if (sameTemplate) {
            uiDataHandle(true);
            return;
          }
        }

        let projectId = selectedTemplate.id;

        let updatedProject = { ...selectedTemplate };
        if (isNewEdgeRunRef.current) {
          updatedProject = {
            ...updatedProject,
            v3: { ...updatedProject.v3, UI: uilist },
          };
        } else {
          updatedProject = { ...updatedProject, v2: { ...updatedProject.v2, UI: uilist } };
        }

        if (func.isEmpty(projectId)) {
          const { updated_project } = await handleSave({ uiObj: uilist, notShowSuccessTip: true });
          setSelectedTemplate(updated_project);
        } else {
          // handle save 注入了 preview app 数据,此处需要额外操作

          await gql.mutation.updateObject(
            parseInt(projectId),
            {
              name: "studio_project",
              attributes: updatedProject,
            },
            ""
          );
          setSelectedTemplate(updatedProject);
        }
        uiDataHandle(false);
      } catch (error) {
        func.messageError(error);
      } finally {
        confirmLoading.value = false;
        logEvent("click_publish_confirm");
      }
    }
  });

  // effect(async () => {
  //   if (needUpdate.value) {
  //     needUpdate.value = false;

  //     selectedCardInfo.value && onChoosePreviewApp(selectedCardInfo.value)
  //   }
  // })

  function uiDataHandle(sameTemplate: boolean) {
    const info = selectedCardInfo.value;
    const isStatic = info?.isStaticMode;

    dispatch(editorActions.setIsStaticMode(!!isStatic));
    resetStartNodeIds(!!isStatic);
    if (!sameTemplate) {
      setSaasUIData(prev => ({ ...prev, input: [], output: [] }));
    }
    if (isStatic) {
      const arr = [
        {
          label: "Static Website",
          value: "saas",
          isStaticApp: true,
        } as CheckedUIListType,
      ];
      if (!sameTemplate) {
        // bsf-4912 use setSassUIData
        setSaasUIData(prev => ({ ...prev, output: [] }));
      }
      setCheckedUIList(arr);
      setSelectedUI(arr[0].value);
    } else {
      autoSelectTemplate(!sameTemplate, !sameTemplate);
    }

    if (func.isEmpty(info) || isStatic == null) {
      return;
    }

    inOrOutPreview(true);
  }

  const onConfirmClicked = () => {
    if (selectedCardInfo.value) {
      onChoosePreviewApp(selectedCardInfo.value);
      logEvent("click_publish_format", {
        format: selectedCardInfo.value.appType,
        event: selectedCardInfo.value.appType,
      });
    }
  };

  /**
   * stud-1722 v2 版本 previewPanel 确认事件
   * @param option
   */
  const onChoosePreviewApp = async (option: PreviewApp.FormatChooseAppType) => {
    const isStatic = option.appType === PreviewAppTypeMap.STATIC_WEB;
    const checkedList = [
      {
        label: "Static Website",
        value: "saas",
        isStaticApp: true,
      } as CheckedUIListType,
    ];

    function uiHandle(sameTemplate = false) {
      dispatch(editorActions.setIsStaticMode(!!isStatic));
      resetStartNodeIds(!!isStatic);
      if (!sameTemplate) {
        setSaasUIData(prev => ({ ...prev, input: [], output: [] }));
      }
      if (isStatic) {
        if (!sameTemplate) {
          // bsf-4912 use setSassUIData
          setSaasUIData(prev => ({ ...prev, output: [] }));
        }
        setCheckedUIList(checkedList);
        setSelectedUI(checkedList[0].value);
        // 不需要自动加入会在 onChoosePreviewApp 开始单独调用
      } else {
        if (option.initLoadInput) {
          autoSelectTemplate(false, true);
        }
      }

      if (isStatic == null) {
        return;
      }

      // inOrOutPreview(true);
      props.backPreview();
    }

    if (option.initLoadInput) {
      // 1. 提前加入 input
      autoSelectTemplate(false, true);
    }

    // 向 appList 增加一条 saasUidata 数据，并修改 title 一些字段
    addPreview({
      format: option,
      uiData: PreviewAppUtil.genPreviewAppTitleByFormat(option),
    });

    // 2. 保存/新建
    let updatedProject = getSelectedTemplate(true);

    if (!updatedProject) {
      // 发生不该出现的错误，没有 template
      return;
    }

    try {
      confirmLoading.value = true;

      if (func.isEmpty(updatedProject.id)) {
        const { updated_project } = await handleSave({ notShowSuccessTip: true });
        setSelectedTemplate(updated_project);
      } else {
        updatedProject = assignPreviewAppData(updatedProject);
        if (!updatedProject) {
          return;
        }

        await gql.mutation.updateObject(
          parseInt(updatedProject.id),
          {
            name: "studio_project",
            attributes: updatedProject,
          },
          ""
        );
        setSelectedTemplate(updatedProject);
      }

      uiHandle(false);
    } catch (error) {
      func.messageError(error);
    } finally {
      confirmLoading.value = false;
    }
  };

  const onClickOption = (option: PreviewApp.FormatChooseAppType) => () => {
    if (confirmLoading.value) return;
    selectedCardInfo.value = option;
  };

  return (
    <div className={style.publishPage}>
      <img
        className={style.leftBackBox}
        onClick={() => props.backPreview()}
        src={`${settings.S3CDN}${settings.viewAssetsPath}ACAEB5Left.svg`}
        width="20"
        alt="icon"
      />
      <div className={style.title}>Choose a format</div>
      <div className={`${style["option-cards-box"]}`}>
        {optionDataList.value.map(option => {
          return (
            option.visible && (
              <div
                className={`
                    ${style["option-card"]}
                    ${option.disabled && style["option-card_disabled"]}
                    ${option === selectedCardInfo.value && style["option-card_selected"]}
                `}
                key={option.title}
                onClick={onClickOption(option)}
              >
                <div className={`${style["option-card-content"]}`}>
                  <div className={`${style["option-card-content-title-line"]}`}>
                    <span
                      className={`${style["option-card-content-title-line-title"]} ${
                        option.disabled && style["option-card_disabled-title"]
                      }`}
                    >
                      {option.title}
                    </span>
                  </div>
                  <div
                    className={`${style["option-card-content-description"]} ${
                      option.disabled && style["option-card_disabled-title"]
                    }`}
                  >
                    {option.describe.map((j, jIdx) => {
                      return <span key={j + jIdx}>{j}</span>;
                    })}
                  </div>
                </div>
                <img
                  className={`${style["option-card-logo"]}`}
                  src={`${settings.S3CDN}${settings.viewAssetsPath}${option.logoSrcName}`}
                  alt=""
                />
              </div>
            )
          );
        })}
      </div>

      <div className={style.confirm}>
        <Button
          type={"primary"}
          shape={"round"}
          className={`${style["confirm-btn"]}`}
          disabled={func.isEmpty(selectedCardInfo.value)}
          loading={confirmLoading.value}
          onClick={onConfirmClicked}
        >
          Confirm
        </Button>
      </div>
    </div>
  );
};

export default Publish;
